// Library Imports
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty, get, noop } from 'lodash';
import {
  BaseCard,
  BaseCardBody,
  Divider,
  Button,
  Icon,
} from '@unite-us/app-components';
import { InfoPanel } from '@unite-us/client-utils';
import { REQUEST_SERVICE_INFO_MESSAGE, CFR_PART_2_CASE_MESSAGE } from 'common/messageConstants';

// Util Imports
import {
  hasPaymentsUserAccess,
  crtb2130MultipleClientNeeds,
  crtb2182ClarifyReferralLanguage,
} from 'common/utils/FeatureFlags/flags';
import { editable } from 'common/utils/Permission/Permission';
import { dividerStatusStyles } from 'common/display/SingleItemDetails/utils/common';
import caseStatus from 'src/components/Cases/utils/caseStatus';
import isCaseOpen from 'src/components/Cases/utils/isCaseOpen';
import generateDetailLabelData from 'common/display/SingleItemDetails/utils/CaseLabel';

// Component Imports
import DocumentUploader from 'common/form/DocumentUploader';
import ClientNeed from 'common/display/SingleItemDetails/dashboard/ClientNeed';
import DetailSummary from 'common/display/SingleItemDetails/dashboard/DetailSummary';
import CaseDetailSummaryWrapper from 'src/components/Cases/components/Detail/CaseDetailSummaryWrapper';
import DetailLabelWrapper from 'common/display/SingleItemDetails/DetailLabelWrapper';
import DetailStatusText from 'common/display/SingleItemDetails/DetailStatusText';
import DetailOutcomeWrapper from 'common/display/SingleItemDetails/DetailOutcomeWrapper';
import DetailActionWrapper from 'common/display/SingleItemDetails/DetailActionWrapper';
import ReferralServiceAuthorizationInformation from 'components/Referrals/ReferralDetail/components/ReferralServiceAuthorizationInformation';
import { referralStatuses, referralStatusNames } from 'src/components/ReferralHistory/utils/referralStatusConstants';
import ServiceCaseActions from './ServiceCaseActions';
import ProgramContactInfo from './ProgramContactInfo';
import {
  INTERNAL_CASES_STATUSES,
} from '../../constants';

import './ServiceCaseOptions.scss';

const ServiceCaseHeader = ({
    title,
    status,
    serviceCase,
    statusComponents,
    statusNames,
  }) => (
    <div className="bg-white rounded-t">
      <div className="flex flex-row justify-between">
        <div className="flex-auto p-4 pb-3">{title()}</div>
        <div className="flex-auto py-2 pr-4 pl-0 flex flex-row justify-end items-center">
          { status && statusComponents[status] && statusComponents[status]({ showText: false }) }
        </div>
        <div className="py-2 pr-4 pl-0 flex flex-row justify-end items-center">
          <span aria-label={`State ${statusNames[status]?.() || 'Unknown'}`}>
            <DetailStatusText enumsKey="service_cases" detailObj={serviceCase} />
          </span>
        </div>
      </div>
    </div>
  );

ServiceCaseHeader.propTypes = {
  title: PropTypes.func.isRequired,
  status: PropTypes.string,
  serviceCase: PropTypes.object.isRequired,
  statusComponents: PropTypes.object.isRequired,
  statusNames: PropTypes.object.isRequired,
};

ServiceCaseHeader.defaultProps = {
  status: '',
};

const useCaseData = (serviceCase, serviceAuthorization) => {
  const contact = serviceCase.person;
  const description = (serviceCase && serviceCase.description) || '';
  const status = caseStatus(serviceCase);
  const isInternalCase = serviceCase && isEmpty(serviceCase.referrals) &&
    INTERNAL_CASES_STATUSES.includes(serviceCase.state);
  const isAuthRequestedOrDenied = serviceAuthorization && ['requested', 'denied'].includes(serviceAuthorization.state);
  const personConditionIds = serviceCase?.person_condition_ids;

  return {
    contact,
    description,
    status,
    isInternalCase,
    isAuthRequestedOrDenied,
    personConditionIds,
  };
};

const createTitle = (contact, isClientsDrawerOpen, setIsClientsDrawerOpen, showOffPlatformChanges) => () => (
  <div className="flex">
    <h1 id="case-title" className="flex items-center">
      {showOffPlatformChanges && (
        <Icon
          icon="OutOfNetwork"
          style={{ svg: { width: '20px', height: '20px', marginRight: '8px' } }}
        />
      )}
      {showOffPlatformChanges ? 'Off-Platform Case for' : 'Case for'}
    </h1>
    <Button
      onClick={() => setIsClientsDrawerOpen(!isClientsDrawerOpen)}
      label={(
        <>
          {contact?.full_name}
          <Icon
            icon="ChevronArrow"
            direction="right"
            color="current"
            className="ml-1 mb-0.5"
            size="16"
          />
        </>
      )}
      omitDefaultStyles
      className="m-0 text-xl text-action-blue hover:underline pl-1 pt-0 pb-0 font-semibold"
      link
      data-testid="case-client-drawer-btn"
      aria-label={`Case for ${contact?.full_name}`}
    />
  </div>
);

function ServiceCaseOptions(props) {
  const {
    currentProviderId,
    groupId,
    groupIsSensitive,
    isCoordinationGroup,
    serviceCase = {},
    type,
    usePaymentsUserRole,
    serviceAuthorization,
    isServiceAuthorizationLoaded,
    fetchCase,
    isClientsDrawerOpen,
    setIsClientsDrawerOpen,
    currentEmployeeId,
    clarifyReferralLanguageFlag,
    multipleClientNeedsFlag,
  } = props;

  const {
    contact,
    description,
    status,
    isInternalCase,
    isAuthRequestedOrDenied,
    personConditionIds,
  } = useCaseData(serviceCase, serviceAuthorization);

  const isOffPlatform = serviceCase.state === 'off_platform';
  const showOffPlatformChanges = isOffPlatform && clarifyReferralLanguageFlag;
  const title = createTitle(contact, isClientsDrawerOpen, setIsClientsDrawerOpen, showOffPlatformChanges);

  const renderClientNeed = () => {
    if (!personConditionIds || personConditionIds.length === 0) return null;

    return (
      <ClientNeed
        contact={contact}
        editable={editable(serviceCase, type, currentProviderId)}
        personConditionIds={personConditionIds}
        caseId={serviceCase?.id}
        reloadCase={fetchCase}
        currentEmployeeId={currentEmployeeId}
        multipleClientNeedsFlag={multipleClientNeedsFlag}
      />
    );
  };

  const renderServiceAuthorization = () => {
    if (!isServiceAuthorizationLoaded) return null;

    return (
      <>
        <Divider className="detail-info__horizontal-divider mb-4 mt-4" />
        <ReferralServiceAuthorizationInformation
          serviceAuthorization={serviceAuthorization}
          showRequestedValues={isInternalCase && isAuthRequestedOrDenied}
          allowRequestChanges={isCaseOpen(serviceCase)}
          reloadCase={fetchCase}
        />
      </>
    );
  };

  const renderProgramsInfo = () => {
    if (!showOffPlatformChanges) return null;

    return (
      <>
        <ProgramContactInfo serviceCase={serviceCase} />
        <Divider className="detail-info__horizontal-divider mb-4 mt-4" />
      </>
    );
  };

  const renderActionSection = () => {
    if (!editable(serviceCase, type, currentProviderId, isCoordinationGroup)) return null;

    return (
      <div className="action-section">
        <Divider className="detail-info__horizontal-divider mb-4 mt-2" />
        <DetailActionWrapper
          buttonComp={(
            <ServiceCaseActions
              contact={contact}
              currentProviderId={currentProviderId}
              isCoordinationGroup={isCoordinationGroup}
              serviceAuthorization={serviceAuthorization}
              serviceCase={serviceCase}
              reloadCase={fetchCase}
            />
          )}
        />
      </div>
    );
  };

  return (
    <BaseCard className="service-case-options detail-info">
      <ServiceCaseHeader
        title={title}
        status={status}
        serviceCase={serviceCase}
        statusComponents={referralStatuses}
        statusNames={referralStatusNames}
      />
      <Divider style={dividerStatusStyles(status)} />
      <BaseCardBody withPadding>
        <DetailOutcomeWrapper status={status} item={serviceCase} />
        <DetailLabelWrapper
          data={generateDetailLabelData(
            serviceCase,
            groupId,
            contact,
            {
              usePaymentsUserRole,
              currentProviderId,
            },
          )}
        />
        <Divider className="mt-4" />
        {renderClientNeed()}
        <CaseDetailSummaryWrapper
          contact={contact}
          serviceCase={serviceCase}
        >
          <DetailSummary
            editable={editable(serviceCase, type, currentProviderId)}
            description={description}
            item={serviceCase}
            descriptionTitle="Case Description"
            descriptionSubtitle={groupIsSensitive ? (
              <InfoPanel
                className="detail-summary__info-panel mb-3"
                message={`Reminder: ${CFR_PART_2_CASE_MESSAGE}`}
              />
            ) : undefined}
            descriptionEditSubtitle={(
              <InfoPanel
                className="service-case-options__info-panel mb-3"
                message={groupIsSensitive ?
                  [REQUEST_SERVICE_INFO_MESSAGE, CFR_PART_2_CASE_MESSAGE].join(' ') :
                  REQUEST_SERVICE_INFO_MESSAGE}
              />
            )}
          />
        </CaseDetailSummaryWrapper>
        {renderServiceAuthorization()}
        <Divider className="detail-info__horizontal-divider mb-4 mt-4" />
        {renderProgramsInfo()}
        <div className="row">
          <div className="col-md-6">
            <h5 className="detail-info__icon-label">Attached Documents</h5>
            <DocumentUploader
              attachedDocuments={serviceCase.documents}
              contactId={contact.id}
              dropzoneName="case-detail-view"
              editable={editable(serviceCase, type, currentProviderId)}
              onAttachDocuments={props.onAttachDocuments}
              onRemoveDocument={props.onRemoveDocument}
              onRenameDocument={props.onRenameDocument}
            />
          </div>
        </div>
        {renderActionSection()}
      </BaseCardBody>
    </BaseCard>
  );
}

ServiceCaseOptions.propTypes = {
  currentProviderId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
  groupIsSensitive: PropTypes.bool,
  isCoordinationGroup: PropTypes.bool.isRequired,
  isServiceAuthorizationLoaded: PropTypes.bool.isRequired,
  onAttachDocuments: PropTypes.func.isRequired,
  onRemoveDocument: PropTypes.func.isRequired,
  onRenameDocument: PropTypes.func.isRequired,
  serviceCase: PropTypes.shape({
    person: PropTypes.shape({
      id: PropTypes.string.isRequired,
      full_name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  serviceAuthorization: PropTypes.object.isRequired,
  type: PropTypes.string,
  usePaymentsUserRole: PropTypes.bool.isRequired,
  fetchCase: PropTypes.func.isRequired,
  isClientsDrawerOpen: PropTypes.bool,
  setIsClientsDrawerOpen: PropTypes.func,
  currentEmployeeId: PropTypes.string.isRequired,
  multipleClientNeedsFlag: PropTypes.bool.isRequired,
  clarifyReferralLanguageFlag: PropTypes.bool.isRequired,
};

ServiceCaseOptions.defaultProps = {
  groupIsSensitive: false,
  type: '',
  isClientsDrawerOpen: false,
  setIsClientsDrawerOpen: noop,
};

ServiceCaseOptions.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    usePaymentsUserRole: hasPaymentsUserAccess(state),
    currentEmployeeId: get(state, 'globalState.currentEmployee.id', ''),
    multipleClientNeedsFlag: crtb2130MultipleClientNeeds(state),
    clarifyReferralLanguageFlag: crtb2182ClarifyReferralLanguage(state),
  };
}

export default connect(mapStateToProps)(ServiceCaseOptions);
